import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useFormContext } from 'react-hook-form';
import { Trans } from 'react-i18next';
import { Typography } from '@components/Typography';
import { InternalLink } from '@components/Link';
import { PhonePreviewFrame } from '@components/PhonePreviewFrame';
import { LOCATION_TYPE } from '@features/ComboLocationSelect';
import { useContactFieldsValues } from '@features/ContactFieldsSettings';
import { useLocations, useTranslation } from '@hooks';
import { RoutePath } from '@constants/routes';
import { PREVIEW_FORM_MESSAGE } from '@constants/forms';
import { InfoOutlinedIcon } from 'assets/images/icons';

const Preview = ({ formId }) => {
    const { watch } = useFormContext();
    const { t } = useTranslation();
    const { locationsMap } = useLocations();
    const brands = useSelector(state => state.brands.data);
    const account = useSelector(state => state.account?.account);
    const iframeRef = useRef(null);
    const [isLoaded, setIsLoaded] = useState(false);

    const locationType = watch('locationType');
    const locationId = watch('locationId');
    const brandId = watch('brandId');
    const texts = watch('texts');
    const contactFields = useContactFieldsValues();

    const getPreviewData = () => {
        const isBrand = locationType === LOCATION_TYPE.BRAND;
        const location = !isBrand && locationId ? locationsMap[locationId] : null;
        const ownerBrandId = isBrand ? brandId : location?.brand?.id;
        const brand = ownerBrandId ? brands?.find(b => b.id === ownerBrandId) : null;

        return {
            id: formId ?? null,
            accountId: account?.id,
            locationId: location ? location.companyId : null,
            brandId: isBrand ? brandId || null : null,
            name: (isBrand ? brand?.title : location?.locationName) || '',
            locale: account?.locale,
            texts,
            ...contactFields,
            appearance: brand?.appearance || null,
        };
    };

    const sendPreviewData = () => {
        iframeRef.current?.contentWindow?.postMessage({ type: PREVIEW_FORM_MESSAGE, data: getPreviewData() }, '*');
    };
    const sendPreviewDataRef = useRef(sendPreviewData);
    sendPreviewDataRef.current = sendPreviewData;

    useEffect(() => {
        const controller = new AbortController();
        const handleMessage = event => {
            if (
                event.origin === import.meta.env.REACT_APP_SURVEY_DOMAIN &&
                event.data.type === 'pl-preview-iframe-loaded'
            ) {
                setIsLoaded(true);
                sendPreviewDataRef.current();
            }
        };
        window.addEventListener('message', handleMessage, { signal: controller.signal });
        return () => controller.abort();
    }, []);

    useEffect(() => {
        if (!isLoaded) return undefined;
        const timer = setTimeout(() => sendPreviewDataRef.current(), 200);
        return () => clearTimeout(timer);
    }, [
        isLoaded,
        locationType,
        locationId,
        brandId,
        texts,
        brands,
        locationsMap,
        account,
        ...Object.values(contactFields),
    ]);

    return (
        <>
            <Typography
                variant="caption"
                sx={{ color: 'var(--text-secondary)', display: 'block', marginBottom: '16px', textAlign: 'center' }}
            >
                <InfoOutlinedIcon style={{ transform: 'translateY(4px)', marginRight: '8px' }} />
                <Trans t={t} i18nKey={'FeedbackSurveys.changeAppearance'}>
                    <InternalLink
                        to={RoutePath.AccountSettingsBrands}
                        sx={{ color: 'var(--primary-main)', cursor: 'pointer', textDecoration: 'none' }}
                    >
                        {{ brandSettings: t('FeedbackSurveys.brandSettings') }}
                    </InternalLink>
                </Trans>
            </Typography>
            <PhonePreviewFrame>
                <iframe
                    ref={iframeRef}
                    title="form-preview"
                    width="100%"
                    height="100%"
                    src={`${import.meta.env.REACT_APP_SURVEY_DOMAIN}/preview/form`}
                    frameBorder="0"
                ></iframe>
            </PhonePreviewFrame>
        </>
    );
};

export default Preview;
